import DOMPurify from 'dompurify';
import Vue, { DirectiveBinding, DirectiveModifiers, ObjectDirective } from 'vue';

// ===================================================
// Sanitize HTML directive
// ===================================================
interface SanitizeHtmlDirective {
	el: HTMLElement;
	value: string;
	oldValue?: string | null;
	modifiers?: DirectiveModifiers;
}

// DOMPurify configuration
const CONFIG_DEFAULT = { USE_PROFILES: { html: true }, FORBID_ATTR: ['style'] };
const CONFIG_STRICT = {
	ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'p', 'br'],
	ALLOWED_ATTR: []
};
const CONFIG_STRIP_ALL = {
	ALLOWED_TAGS: [],
	ALLOWED_ATTR: []
};

/**
 * Get the DOMPurify configuration based on the modifiers
 *
 * @param {DirectiveModifiers} [modifiers]
 * @return {Record<string, any>}
 */
function resolveSanitizeHtmlModifiers(modifiers?: DirectiveModifiers): Record<string, any> {
	if (modifiers?.default) {
		return CONFIG_DEFAULT;
	}
	else if (modifiers?.strict) {
		return CONFIG_STRICT;
	}
	else if (modifiers?.stripAll) {
		return CONFIG_STRIP_ALL;
	}
	return CONFIG_DEFAULT;
}

/**
 * Sanitize the HTML content of an element
 *
 * @param {SanitizeHtmlDirective} param0
 * @param {HTMLElement} param0.el - The element to sanitize
 * @param {string} param0.value - The value to sanitize
 * @param {string} [param0.oldValue] - The old value to compare with
 * @param {DirectiveModifiers} [param0.modifiers] - The modifiers to use for sanitization (default, strict, stripAll)
 * @return {void}
 */
function sanitize({ el, modifiers, value, oldValue }: SanitizeHtmlDirective): void {
	if (value === oldValue) {
		return;
	}
	const config = resolveSanitizeHtmlModifiers(modifiers);

	el.innerHTML = DOMPurify.sanitize(value, config);
}

export const sanitizeHtmlDirective: ObjectDirective = {
	bind: (el: HTMLElement, { value, modifiers }: DirectiveBinding<string>) => sanitize({ el, value, modifiers }),
	inserted: (el: HTMLElement, { value, modifiers }: DirectiveBinding<string>) => sanitize({ el, value, modifiers }),
	update: (el: HTMLElement, { value, oldValue, modifiers }: DirectiveBinding<string>) => sanitize({ el, value, oldValue, modifiers }),
	componentUpdated: (el: HTMLElement, { value, oldValue, modifiers }: DirectiveBinding<string>) => sanitize({ el, value, oldValue, modifiers })
};


// Directive registration
Vue.directive('sanitize-html', sanitizeHtmlDirective);